// src/pages/Auth/Signup.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

function SignUp() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirm_password: '',
        first_name: '',
        last_name: '',
        activity_field: '',
        country: '',
    });
    
    const [choices, setChoices] = useState({ 
        countries: [], 
        activity_fields: [] 
    });
    
    const [errors, setErrors] = useState({});
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchChoices();
    }, []);

    const fetchChoices = async () => {
        try {
            const response = await api.get('/users/form-choices/'); 
            setChoices(response.data);
        } catch (error) {
            console.error("Failed to fetch form choices:", error);
            setErrors({ form: t('signup.errors.failed_to_load_form_options') });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Email validation
        if (!formData.email) {
            newErrors.email = t('signup.errors.email_required');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('signup.errors.email_invalid');
        }
        
        // Password validation
        if (!formData.password) {
            newErrors.password = t('signup.errors.password_required');
        } else if (formData.password.length < 8) {
            newErrors.password = t('signup.errors.password_too_short');
        }
        
        // Confirm password validation
        if (!formData.confirm_password) {
            newErrors.confirm_password = t('signup.errors.confirm_password_required');
        } else if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = t('signup.errors.passwords_do_not_match');
        }
        
        // Country validation
        if (!formData.country) {
            newErrors.country = t('signup.errors.country_required');
        }
        
        // Terms validation
        if (!agreeTerms) {
            newErrors.terms = t('signup.errors.must_agree_terms');
        }
        
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        
        setErrors({});
        setLoading(true);

        try {
            // Send registration request
            await api.post('/users/register/', {
                email: formData.email.toLowerCase(),
                password: formData.password,
                confirm_password: formData.confirm_password,
                first_name: formData.first_name,
                last_name: formData.last_name,
                activity_field: formData.activity_field,
                country: formData.country,
            });
            
            setSuccessMessage(t('signup.confirmation.code_sent'));
            setShowConfirmation(true);
        } catch (error) {
            if (error.response?.data) {
                setErrors(error.response.data);
            } else {
                setErrors({ form: t('signup.errors.unexpected_error') });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmation = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            await api.post('/users/confirm/', {
                email: formData.email.toLowerCase(),
                code: confirmationCode
            });
            
            setSuccessMessage(t('signup.confirmation.success'));
            setTimeout(() => {
                navigate('/login', { 
                    state: { message: t('signup.errors.invalid_confirmation_code') } 
                });
            }, 2000);
        } catch (error) {
            if (error.response?.data?.error) {
                setErrors({ confirmation: error.response.data.error });
            } else {
                setErrors({ confirmation: t('Invalid confirmation code. Please try again.') });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setLoading(true);
        setErrors({});
        setSuccessMessage('');
        
        try {
            await api.post('/users/resend-confirmation/', {
                email: formData.email.toLowerCase()
            });
            setSuccessMessage(t('signup.confirmation.code_sent'));
        } catch (error) {
            setErrors({ form: t('signup.errors.failed_to_resend_code') });
        } finally {
            setLoading(false);
        }
    };

    // Confirmation Screen
    if (showConfirmation) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-brand-dark hover:border-brand-orange text-white px-4">
                <div className="p-8 bg-slate-800 rounded-lg shadow-md w-full max-w-md border border-slate-700">
                    <h2 className="text-2xl font-bold text-center mb-6">{t('signup.confirmation.title')}</h2>
                    
                    {successMessage && (
                        <div className="mb-4 p-3 bg-green-900/50 border border-green-600 hover:border-orange-500 rounded-md text-green-200 text-sm">
                            {successMessage}
                        </div>
                    )}
                    
                    {errors.form && (
                        <div className="mb-4 p-3 bg-red-900/50 border border-red-600 rounded-md text-red-200 text-sm">
                            {errors.form}
                        </div>
                    )}
                    
                    <form onSubmit={handleConfirmation}>
                        <div className="mb-4">
                            <label className="block text-orange-400 font-light mb-1" htmlFor="code">
                                 {t('signup.confirmation.enter_code')}
                            </label>
                            <input
                                type="text"
                                id="code"
                                value={confirmationCode}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                                    setConfirmationCode(value);
                                    if (errors.confirmation) {
                                        setErrors({});
                                    }
                                }}
                                placeholder={t('signup.confirmation.code_placeholder')}
                                maxLength="6"
                                className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-center text-2xl tracking-widest"
                            />
                            {errors.confirmation && (
                                <p className="text-red-400 text-sm mt-1">{errors.confirmation}</p>
                            )}
                            <p className="text-gray-400 text-xs mt-2">
                                {t('signup.confirmation.check_email')}
                            </p>
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading || confirmationCode.length !== 6}
                            className="w-full bg-orange-500 text-white py-2 rounded-md hover:bg-orange-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? t('signup.confirmation.confirming') : t('signup.confirmation.confirm')}
                        </button>
                    </form>

                    <button
                        onClick={handleResendCode}
                        disabled={loading}
                        className="w-full mt-3 bg-slate-700 text-white py-2 rounded-md hover:bg-slate-600 transition-colors disabled:opacity-50"
                    >
                        {loading ? t('signup.confirmation.sending') : t('signup.confirmation.resend_code')}
                    </button>

                    <div className="mt-4 text-center">
                        <button
                            onClick={() => setShowConfirmation(false)}
                            className="text-sm text-orange-400 hover:underline"
                        >
                            {t('signup.confirmation.back_to_signup')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Main Signup Form
    return (
        <div className="flex items-center justify-center min-h-screen bg-brand-dark text-white px-4 py-8">
            <div className="p-8 bg-brand-dark rounded-lg shadow-md w-full max-w-md border border-slate-700 hover:border-brand-orange">
                <h2 className="text-2xl font-bold text-center mb-6">{t('signup.title')}</h2>
                
                {errors.form && (
                    <div className="mb-4 p-3 bg-red-900/50 border border-red-600 rounded-md text-red-200 text-sm">
                        {errors.form}
                    </div>
                )}
                
                <form onSubmit={handleSubmit} noValidate>
                    {/* Email */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="email">
                            {t('signup.form.email')} <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={t('signup.form.email_placeholder')}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                    </div>

                    {/* Password */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="password">
                             {t('signup.form.password')} <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder={t('signup.form.password_placeholder')}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
                    </div>

                    {/* Confirm Password */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="confirm_password">
                            {t('signup.form.confirm_password')} <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="password"
                            id="confirm_password"
                            name="confirm_password"
                            value={formData.confirm_password}
                            onChange={handleChange}
                            placeholder={t('signup.form.confirm_password_placeholder')}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        {errors.confirm_password && (
                            <p className="text-red-400 text-sm mt-1">{errors.confirm_password}</p>
                        )}
                    </div>

                    {/* First Name */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="first_name">
                            {t('signup.form.first_name')} <span className="text-gray-500 text-sm">(optional)</span>
                        </label>
                        <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            placeholder={t('signup.form.first_name_placeholder')}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>

                    {/* Last Name */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="last_name">
                            {t('signup.form.last_name')} <span className="text-gray-500 text-sm">(optional)</span>
                        </label>
                        <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            placeholder={t('signup.form.last_name_placeholder')}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>

                    {/* Activity Field */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="activity_field">
                            {t('signup.form.activity_field')} <span className="text-gray-500 text-sm">({t('optional')})</span>
                        </label>
                        <select
                            id="activity_field"
                            name="activity_field"
                            value={formData.activity_field}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">Select your field</option>
                            {choices.activity_fields.map((field) => (
                                <option key={field.value} value={field.value}>
                                    {field.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Country */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1" htmlFor="country">
                            {t('signup.form.country')} <span className="text-red-400">*</span>
                        </label>
                        <select
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">{t('signup.form.select_country')}</option>
                            {choices.countries.map((country) => (
                                <option key={country.code} value={country.code}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                        {errors.country && <p className="text-red-400 text-sm mt-1">{errors.country}</p>}
                    </div>

                    {/* Terms & Privacy */}
                    <div className="mb-6">
                        <div className="flex items-start">
                            <input
                                type="checkbox"
                                id="agree_terms"
                                checked={agreeTerms}
                                onChange={(e) => {
                                    setAgreeTerms(e.target.checked);
                                    if (errors.terms) {
                                        setErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.terms;
                                            return newErrors;
                                        });
                                    }
                                }}
                                className="mr-2 h-4 w-4 mt-1 rounded bg-slate-700 border-slate-600 text-orange-500 focus:ring-orange-500"
                            />
                            <label htmlFor="agree_terms" className="text-sm text-gray-300">
                                {t('signup.form.agree_to')}{' '}
                                <Link to="/terms" className="hover:underline text-orange-400">
                                    {t('terms_of_service')}
                                </Link>
                                {' &'}{t('signup.form.Artikeldeutch')}
                                <Link to="/privacy" className="hover:underline text-orange-400">
                                    {t('privacy_policy')}
                                </Link>
				    {t('signup.form.Artikelzu')}
                            </label>
                        </div>
                        {errors.terms && <p className="text-red-400 text-sm mt-1">{errors.terms}</p>}
                    </div>

                    {/* Submit Button */}
                    <button 
                        type="submit" 
                        disabled={loading}
                        className="w-full bg-brand-orange text-white py-2 rounded-md hover:bg-orange-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('signup.form.signing_up')}
                            </span>
                        ) : (
                            t('signup.form.sign_up')
                        )}
                    </button>
                </form>
                
                {/* Login Link */}
                <div className="mt-6 text-center">
                    <p className="text-gray-400">
                        {t('signup.form.already_have_account')}{' '}
                        <Link to="/login" className="text-orange-400 hover:underline">
                            {t('Login')}
                        </Link>
                    </p>
                </div>

                {/* Footer Links */}
                <div className="mt-4 flex justify-center space-x-4 text-sm">
                    <Link 
                        to="/terms" 
                        className="text-orange-400 hover:underline"
                    >
                        {t('terms_of_use')}
                    </Link>
                    <span className="text-gray-600">|</span>
                    <Link 
                        to="/privacy" 
                        className="text-orange-400 hover:underline"
                    >
                       {t('privacy_policy')}
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default SignUp;
